import { serializeParams } from "@/utils/apiUtils";
import { AxiosPromise } from "axios";
import { ApiRequestAdapter } from "../../../common/api/ApiRequestAdapter";
import { apiService } from "../../../common/api/apiService";
import { HttpMethod } from "../../../common/api/http";
import { EntityIdObject, EntityObject, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../../common/types";
import { AGENT_FILTER_INCLUSION_DEFAULTS, COMMON_AGENT_FILTER_QUERY_PARAMS } from "../../agent/api";
import {
  BailAccountMovement,
  BailAccountMovementFilterPageRequest,
  BailAccountMovementFilterPageResult,
  BailAccountSettings,
  BailAccountsReportFilterExportRequest,
  BailAccountsReportFilterPageRequest,
  BailAccountsReportFilterPageResult,
  CreateUpdateBailAccountMovement,
  CreateUpdateBailAccountSettings,
} from "./types";

const GET_BAIL_ACCOUNTS_REPORT = ApiRequestAdapter.create("/bail-accounts-report", HttpMethod.GET);
const GET_BAIL_ACCOUNTS_EXPORT = ApiRequestAdapter.create("/bail-accounts-report/export", HttpMethod.GET);

const CREATE_BAIL_ACCOUNT_SETTINGS = ApiRequestAdapter.create("/agents/{0}/bail-account/settings", HttpMethod.POST);
const UPDATE_BAIL_ACCOUNT_SETTINGS = ApiRequestAdapter.create("/agents/{0}/bail-account/settings", HttpMethod.PUT);
const DELETE_BAIL_ACCOUNT_SETTINGS = ApiRequestAdapter.create("/agents/{0}/bail-account/settings", HttpMethod.DELETE);

const FILTER_BAIL_ACCOUNT_MOVEMENTS = ApiRequestAdapter.create("/agents/{0}/bail-account/movements", HttpMethod.GET);
const CREATE_BAIL_ACCOUNT_MOVEMENT = ApiRequestAdapter.create("/agents/{0}/bail-account/movements", HttpMethod.POST);
const UPDATE_BAIL_ACCOUNT_MOVEMENT = ApiRequestAdapter.create("/agents/{0}/bail-account/movements/{1}", HttpMethod.PUT);
const DELETE_BAIL_ACCOUNT_MOVEMENT = ApiRequestAdapter.create(
  "/agents/{0}/bail-account/movements/{1}",
  HttpMethod.DELETE,
);

export const bailAccountApi = {
  getBailAccountsReport: (
    request: BailAccountsReportFilterPageRequest,
  ): AxiosPromise<BailAccountsReportFilterPageResult> => {
    return apiService.get(GET_BAIL_ACCOUNTS_REPORT.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  downloadBailAccountsExport: (request: BailAccountsReportFilterExportRequest): AxiosPromise<Blob> => {
    return apiService.get(GET_BAIL_ACCOUNTS_EXPORT.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
      responseType: "blob",
    });
  },
  createBailAccountSettings: (
    request: EntityObject<CreateUpdateBailAccountSettings>,
  ): AxiosPromise<BailAccountSettings> => {
    return apiService.post(CREATE_BAIL_ACCOUNT_SETTINGS.getParametrizedUrl(request.id), request.object);
  },
  updateBailAccountSettings: (
    request: EntityObject<CreateUpdateBailAccountSettings>,
  ): AxiosPromise<BailAccountSettings> => {
    return apiService.put(UPDATE_BAIL_ACCOUNT_SETTINGS.getParametrizedUrl(request.id), request.object);
  },
  deleteBailAccountSettings: (request: EntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_BAIL_ACCOUNT_SETTINGS.getParametrizedUrl(request.id));
  },
  filterBailAccountMovements: (
    request: EntityObject<BailAccountMovementFilterPageRequest>,
  ): AxiosPromise<BailAccountMovementFilterPageResult> => {
    return apiService.get(FILTER_BAIL_ACCOUNT_MOVEMENTS.getParametrizedUrl(request.id), {
      params: request.object,
    });
  },
  createBailAccountMovement: (
    request: EntityObject<CreateUpdateBailAccountMovement>,
  ): AxiosPromise<BailAccountMovement> => {
    return apiService.post(CREATE_BAIL_ACCOUNT_MOVEMENT.getParametrizedUrl(request.id), request.object);
  },
  updateBailAccountMovement: (
    request: TwoLevelEntityObject<CreateUpdateBailAccountMovement>,
  ): AxiosPromise<BailAccountMovement> => {
    return apiService.put(UPDATE_BAIL_ACCOUNT_MOVEMENT.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteBailAccountMovement: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_BAIL_ACCOUNT_MOVEMENT.getParametrizedUrl(request.id1, request.id2));
  },
};

export const DEFAULT_BAIL_ACCOUNTS_REPORT_QUERY_PARAMS: Required<BailAccountsReportFilterPageRequest> = {
  ...COMMON_AGENT_FILTER_QUERY_PARAMS,
  ...AGENT_FILTER_INCLUSION_DEFAULTS.NONE_INCLUDED,
  balanceAsOfDate: "",
  categories: [],
  includeUnprocessed: false,
};
